import React, { use } from 'react';
import { RiVideoAiFill, RiImageAiFill } from 'react-icons/ri';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { type UserImage } from '@/api/imageApi';
import { normalizeAspectRatio } from '@/utils/common/imageUtils';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import { getEmailBadgeStyle } from '@/components/ui/styles/badgeStyles';
import { getStatusClasses } from '@/components/ui/common/GallerySharedComponents';
import { useToggleImageFavourite } from '@/hooks/querry_hooks/common/useFavouriteQuery';
// import { useToggleImageArchive } from '@/hooks/querry_hooks/common/useArchiveQuery';

interface ImageTileProps {
  /** The image data to display */
  readonly image: UserImage;
  /** Handler for image click */
  readonly onClick?: (image: UserImage) => void;
  /** Whether to show user email badge (for admin views) */
  readonly showUserEmail?: boolean;
  /** Handler for attaching image to input with tab specification */
  readonly onAttach?: (image: UserImage, tab: 'Videos' | 'Images') => void;
  /** Whether this is the favourites page (always show solid heart) */
  readonly isFavouritesPage?: boolean;
  /** Whether this is the archives page (hide pin button) */
  readonly isArchivesPage?: boolean;
  /** Handler for favourite toggle success */
  readonly onFavouriteSuccess?: (isFavourited: boolean) => void;
  /** Handler for archive toggle success */
  readonly onArchiveSuccess?: (isArchived: boolean) => void;
  /** Whether the user is an admin (shows archive button) */
  readonly isAdmin?: boolean;
}

/**
 * Shared individual image tile component
 * Optimized for virtualization with React.memo
 * Used by both dashboard and admin content review pages
 * 
 * Features:
 * - Model badge with aspect ratio
 * - Status badge
 * - User email badge (optional, for admin views)
 * - Responsive styling
 * - Keyboard accessibility
 * - Lazy image loading
 */
const ImageTile: React.FC<ImageTileProps> = React.memo(({ 
  image, 
  onClick,
  showUserEmail = true,
  onAttach,
  isFavouritesPage = false,
  isArchivesPage: _isArchivesPage = false, // Not used for images but kept for consistency with video components
  onFavouriteSuccess,
  // onArchiveSuccess,
  // isAdmin = false,
}) => {
  const { isMobile } = use(DeviceContext) ?? { isMobile: false };
  const emailBadgeStyle = getEmailBadgeStyle(isMobile);
  
  // Get favourite status from image prop
  const [isFavorited, setIsFavorited] = React.useState(image.is_favourited ?? false);
  
  // Commented out archive status
  // const [isArchived, setIsArchived] = React.useState(image.is_archived ?? false);
  
  // Get the mutation hook for toggling favourites
  const toggleFavouriteMutation = useToggleImageFavourite(
    (isFavourited: boolean) => {
      if (onFavouriteSuccess) {
        onFavouriteSuccess(isFavourited);
      }
    }
  );
  
  // Commented out archive mutation hook
  // const toggleArchiveMutation = useToggleImageArchive(
  //   (isArchived: boolean) => {
  //     // Update local state when archive succeeds
  //     setIsArchived(isArchived);
  //     // Call the success callback if provided
  //     if (onArchiveSuccess) {
  //       onArchiveSuccess(isArchived);
  //     }
  //   }
  // );

  // Update when image prop changes
  // If on favourites page, always show as favorited
  React.useEffect(() => {
    setIsFavorited(isFavouritesPage ? true : (image.is_favourited ?? false));
  }, [image.is_favourited, isFavouritesPage]);
  
  // Commented out archive status update
  // React.useEffect(() => {
  //   setIsArchived(image.is_archived ?? false);
  // }, [image.is_archived]);

  const handleAttachClick = (e: React.MouseEvent, tab: 'Videos' | 'Images') => {
    e.stopPropagation();
    if (onAttach) {
      onAttach(image, tab);
    }
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Use the mutation to toggle favourite (handles optimistic updates automatically)
    toggleFavouriteMutation.mutate(image.id);
  };

  // Commented out archive toggle handler
  // const handleToggleArchive = async (e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   
  //   // Use the mutation to toggle archive (handles optimistic updates automatically)
  //   toggleArchiveMutation.mutate(image.id);
  // };

  return (
    <button
      type="button"
      className="bg-gray-200 rounded-xl overflow-hidden hover:opacity-90 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl relative group w-full h-full border-0 p-0"
      style={{ aspectRatio: '1/1' }}
      onClick={() => onClick?.(image)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(image);
        }
      }}
      aria-label={`View image: ${image.title}`}
    >
      <div className="w-full h-full relative">
        <img
          src={image.image_url}
          alt={image.title}
          className="w-full h-full object-cover bg-gray-100"
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          onError={(e) => {
            e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTQgMTZsNC41ODYtNC41ODZhMiAyIDAgMDEyLjgyOCAwTDE2IDE2bS0yLTJsMS41ODYtMS41ODZhMiAyIDAgMDEyLjgyOCAwTDIwIDE0bS02LTZoLjAxTTYgMjBoMTJhMiAyIDAgMDAyLTJWNmEyIDIgMCAwMC0yLTJINmEyIDIgMCAwMC0yIDJ2MTJhMiAyIDAgMDAyIDJ6IiBzdHJva2U9IiM5Q0E3QjQiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=';
          }}
        />

        {/* Image Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex flex-col justify-between">
          {/* Attach Icons and Favorite - Center of image on hover */}
          <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            {/* Attach Icons - Only show if onAttach is provided */}
            {onAttach && (
              <>
                <button
                  onClick={(e) => handleAttachClick(e, 'Videos')}
                  type="button"
                  className="bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200 hover:scale-110 pointer-events-auto"
                  title="Attach to Videos Tab"
                  aria-label="Attach to Videos Tab"
                >
                  <RiVideoAiFill className="text-white text-xl" />
                </button>
                <button
                  onClick={(e) => handleAttachClick(e, 'Images')}
                  type="button"
                  className="bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200 hover:scale-110 pointer-events-auto"
                  title="Attach to Images Tab"
                  aria-label="Attach to Images Tab"
                >
                  <RiImageAiFill className="text-white text-xl" />
                </button>
              </>
            )}
            
            {/* Favorite Button - Always show for all users EXCEPT on archives page */}
            {!_isArchivesPage && (
              <button
                onClick={handleToggleFavorite}
                type="button"
                className="bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200 hover:scale-110 pointer-events-auto"
                title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
              >
                {isFavorited ? (
                  <Favorite className="text-white text-xl" />
                ) : (
                  <FavoriteBorder className="text-white text-xl" />
                )}
              </button>
            )}
            
            {/* Commented out Archive Button */}
            {/* {isAdmin && (
              <button
                onClick={handleToggleArchive}
                type="button"
                className="bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200 hover:scale-110 pointer-events-auto"
                title={isArchived ? "Remove from archive" : "Add to archive"}
                aria-label={isArchived ? "Remove from archive" : "Add to archive"}
              >
                {isArchived ? (
                  <Archive className="text-white text-xl" />
                ) : (
                  <ArchiveOutlined className="text-white text-xl" />
                )}
              </button>
            )} */}
          </div>
          {/* Top section with badges */}
          <div className="flex items-start justify-between gap-2" style={{ padding: '10px' }}>
            {/* Model badge with aspect ratio */}
            <div
              className="bg-gray-800 bg-opacity-90 text-white flex items-center"
              style={{
                borderRadius: '30px',
                padding: '5px 10px',
                height: '30px',
                minWidth: '100px',
                flex: '1 1 auto',
                maxWidth: '20%',
                justifyContent: 'space-between',
                fontFamily: 'Maven Pro',
                fontWeight: 400,
                fontSize: '11px',
                lineHeight: '18px',
                gap: '4px'
              }}
            >
              <span style={{ flexShrink: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {(image.model || 'Gemini').replace('Gemini 2.5', 'Gemini2.5').replace('Gemini 2.0', 'Gemini2.0').replace('Gemini 2', 'Gemini2')}
              </span>
              <svg
                width="16"
                height="9"
                viewBox="0 0 16 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexShrink: 0 }}
              >
                <rect x="0.5" y="0.5" width="15" height="8" rx="0.5" stroke="white" fill="transparent" />
              </svg>
              <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                {normalizeAspectRatio(image.aspect_ratio) || '1:1'}
              </span>
            </div>

            {/* Status badge */}
            <div style={{ width: '90px' }}>
              {image.status && (
                <div
                  className={`flex items-center justify-center ${getStatusClasses(image.status)}`}
                  style={{
                    borderRadius: '30px',
                    paddingTop: '5px',
                    paddingRight: '10px',
                    paddingBottom: '5px',
                    paddingLeft: '10px',
                    height: '30px',
                    fontFamily: 'Maven Pro',
                    fontWeight: 400,
                    fontSize: '11px',
                    lineHeight: '18px',
                    letterSpacing: '0%'
                  }}
                >
                  {image.status?.charAt(0).toUpperCase() + image.status?.slice(1).toLowerCase()}
                </div>
              )}
            </div>

            {/* User email badge - optional, for admin views */}
            {showUserEmail && image.user_email && (
              <div
                className="bg-gray-800 bg-opacity-90 text-white"
                style={emailBadgeStyle}
              >
                <span className="truncate" style={{ maxWidth: '103px' }}>
                  {image.user_email}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
});

ImageTile.displayName = 'ImageTile';

export default ImageTile;
